import {
  isNil,
  map,
  reject
} from 'ramda'

import {
  ContactsAPI,
  ContactsMergerAPI,
  ConversationAPI,
  CustomerAPI,
  SyncAPI
} from 'api'

export const getContacts = (
  orgId, size = 20, startFrom = 0, filter = {}
) => {
  return CustomerAPI.post(`/organizations/${orgId}/end_users/search`, filter, {
    params: {
      size,
      from: startFrom
    }
  })
}

export const searchContactList = (size = 25, startFrom = 0) => (
  CustomerAPI.post(`/segment/search`, {}, {
    params: {
      size,
      from: startFrom
    }
  })
)

export const getImports = (size = 20, startFrom = 0) => (
  CustomerAPI.get(`/csvimport/paginated`, {
    params: {
      count: size,
      offset: startFrom
    }
  })
)

export const getContact = (id) => (
  CustomerAPI.get(`/end_users/${id}`)
)

export const updateContact = attributes => (
  CustomerAPI.post('/contacts/attributes', attributes)
)

export const followContact = (id, isFollowing) => (
  CustomerAPI.patch(`/end_users/${id}/following`, { following: isFollowing })
)

export const checkFollowStatus = (id) => (
  CustomerAPI.get(`/end_users/${id}/following`)
)

export const unsubscribeContact = (email) => (
  CustomerAPI.post(`/unsubscribe/mark`, { email })
)

export const checkUnsubscribeStatus = (orgId, email) => (
  CustomerAPI.post(`/unsubscribe/check`, {
    orgId,
    email
  })
)

export const getContactTimeline = ({
  contactId,
  offset = 0,
  size = 20,
  startMillis = null,
  filter = null,
  resolveMaster=true
}) => {
  const params = reject(isNil, {
    size,
    offset,
    startMillis,
    resolveMaster,
    filter
  })

  return CustomerAPI.get(`/end_users/${contactId}/timeline`, { params })
}

export const postNewContactNote = (contactId, note) => (
  CustomerAPI.post(`/end_users/${contactId}/notes`, { body: `<p>${note}</p>` })
)

export const getSingleSegment = (segmentId) => (
  CustomerAPI.get(`/user_lists/${segmentId}`)
)

export const getSingleStaticSegmentList = ({
  id,
  size = 20,
  startFrom = 0
}) => (
  CustomerAPI.get(`/user_lists/${id}/members`, {
    params: {
      size,
      from: startFrom
    }
  })
)

export const getSingleDynamicSegmentList = ({
  id,
  query,
  size = 20,
  startFrom = 0
}) => (
  CustomerAPI.post(`/organizations/${id}/end_users/search`, { ...query }, {
    params: {
      size,
      from: startFrom
    }
  })
)

export const getSingleMarketoSegmentList = ({
  id,
  query,
  size = 20,
  startFrom = 0
}) => (
  CustomerAPI.get(`/user_lists/${id}/members`, {
    params: {
      size,
      from: startFrom
    }
  })
)

export const getSinglePardotSegmentList = ({
  id,
  query,
  size = 20,
  startFrom = 0
}) => (
  CustomerAPI.get(`/user_lists/${id}/members`, {
    params: {
      size,
      from: startFrom
    }
  })
)

export const getSingleHubspotSegmentList = ({
  id,
  query,
  size = 20,
  startFrom = 0
}) => (
  CustomerAPI.get(`/user_lists/${id}/members`, {
    params: {
      size,
      from: startFrom
    }
  })
)

export const getSingleMarketoSegmentCount = ({ id }) => (
  SyncAPI.get(`/marketo/segments/membership/count`, { params: { id } })
)

export const getSinglePardotSegmentCount = ({ id }) => (
  SyncAPI.get(`/pardot/segments/membership/count`, { params: { id } })
)

export const getSingleHubspotSegmentCount = ({ id }) => (
  SyncAPI.get(`/hubspot/segments/membership/count`, { params: { id } })
)

export const getSegmentMemberships = (contactId, orgId) => (
  CustomerAPI.get(`/end_users/${contactId}/memberships`, { params: { orgId } })
)

export const getBulkSegments = (list, orgId) => (
  CustomerAPI.get(`/user_lists`, {
    params: {
      orgId,
      id: list
    }
  })
)

export const archiveContactGroup = async (selectedContacts) => {
  const archiveGroup = map((id) => CustomerAPI.patch(`/end_users/${id}`, { status: 'ARCHIVED' }), selectedContacts)
  return Promise.all(archiveGroup)
}

export const archiveSingleContact = (id) => (
  CustomerAPI.patch(`/end_users/${id}`, { status: 'ARCHIVED' })
)

export const createNewContact = (contact, orgId) => (
  CustomerAPI.post(`/organizations/${orgId}/end_users/v2`, contact)
)


export const unsubCSV = () => (
  CustomerAPI.get(`/unsubscribe/csv`)
)

export const getContactToEnroll = async endUserIds => (
  CustomerAPI.post(`end_users/find`, { endUserIds })
)

export const getAllContactProperties = async () => (
  CustomerAPI.get(`/property_definitions`)
)

export const getDriftProperties = async () => (
  CustomerAPI.get(`/contacts/attributes/retrieve/system`) // drift attributes
)

export const getSystemProperties = async () => (
  CustomerAPI.get(`/contacts/attributes/retrieve/programmatic`)
)

export const getAllContactEvents = async (orgId) => (
  CustomerAPI.get(`/organizations/${orgId}/end_users/attributes2`, { params: { playbookEventInclude: false } })
)

export const getAllTags = async () => (
  CustomerAPI.get(`/contacts/tags`)
)

export const getGeoIPProperties = endUserId => (
  ContactsAPI.get(`/end_user/${endUserId}/properties/geoip`)
)

export const getSalesforceSourcedProperties = async () => (
  ConversationAPI.get('/salesforce/sync/sourced/fields')
)

export const getDriftIntel = (endUserId, targets) => {
  return ContactsAPI.post('/derivation', {
    endUserId,
    targets
  })
}

export const updateContactPropertyStatus = ({
  id,
  status
}) => (
  CustomerAPI.patch(`/property_definitions/${id}/single/update_status?status=${status}`)
)

export const getDuplicates =   ({
  size = 20,
  from = 0,
  q = null,
  sortAscended = false,
  sortField = null
}) => {

  const params = reject(isNil, {
    size,
    from ,
    q,
    sortAscended,
    sortField
  })

  return ContactsMergerAPI.get(`/candidates`, { params })
}


export const getMergeCandidate = ({ email }) => {
  return ContactsMergerAPI.get(`/candidates/${email}/preview`)
}

export const ignoreCandidate = ({ email }) => {
  return ContactsMergerAPI.post(`/candidates/ignore`, [email])
}

export const mergeContact = ({
  email,
  preview
}) => {
  return ContactsMergerAPI.post(`/candidates/${email}/merge`, preview)
}

export const getNextCandidate = ({ excludedEmails }) => {
  return ContactsMergerAPI.post(`/candidates/preview/next`, { excludedEmails })
}
